import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, ArrowRight, Sparkles, Building } from 'lucide-react';
import { congresoPublicoApi } from '../services/congresoPublicoApi';
import { CongresoEdicionHistorica } from '../types/congreso.types';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

export const EdicionesHistoricasView: React.FC = () => {
  const [ediciones, setEdiciones] = useState<CongresoEdicionHistorica[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cargarEdiciones = async () => {
      try {
        setLoading(true);
        const data = await congresoPublicoApi.getEdicionesHistoricas();
        setEdiciones(data);
      } catch (err) {
        console.error('Error cargando ediciones históricas:', err);
      } finally {
        setLoading(false);
      }
    };

    cargarEdiciones();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col relative overflow-x-hidden">
      {/* Background Decorativo sutil idéntico al micrositio */}
      <div className="absolute inset-0 opacity-15 pointer-events-none bg-[radial-gradient(#00A3E0_1px,transparent_1px)] [background-size:24px_24px]"></div>

      <Navbar />

      <main className="flex-1 pt-32 pb-24 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-50 border border-brand-cyan/30 text-brand-navy text-xs font-bold uppercase tracking-wider mb-4 shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-brand-cyan" />
              Memoria Institucional
            </div>
            <h1 className="text-3xl sm:text-5xl font-black text-brand-navy tracking-tight leading-tight">
              Archivo Histórico de Congresos
            </h1>
            <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Reviví las ediciones anteriores, disertaciones, memorias y avances del Congreso Inmobiliario de la Provincia de Buenos Aires.
            </p>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-500">
              <div className="w-12 h-12 border-4 border-brand-cyan/20 border-t-brand-cyan rounded-full animate-spin mb-4"></div>
              <span className="text-sm font-semibold tracking-wide text-brand-navy">Cargando ediciones...</span>
            </div>
          ) : ediciones.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {ediciones.map((edicion) => (
                <div
                  key={edicion.idCongreso}
                  className="card-blueprint rounded-3xl p-8 flex flex-col justify-between group relative overflow-hidden bg-white border border-slate-200 hover:border-brand-cyan"
                >
                  {/* Marca de agua institucional de fondo */}
                  <div className="absolute -top-4 -right-4 p-6 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none">
                    <Building className="w-32 h-32 text-brand-navy" />
                  </div>

                  <div className="relative z-10">
                    {edicion.imagenLogoUrl && (
                      <div className="mb-6 h-16 flex items-center">
                        <img
                          src={edicion.imagenLogoUrl}
                          alt={edicion.nombre}
                          className="max-h-full max-w-[180px] object-contain"
                        />
                      </div>
                    )}

                    <div className="inline-block px-3 py-1 rounded-full bg-sky-50 border border-brand-cyan/30 text-brand-navy text-xs font-bold mb-3 shadow-xs">
                      Edición {edicion.anio}
                    </div>

                    <h2 className="text-2xl font-bold text-brand-navy mb-3 group-hover:text-brand-cyan transition-colors leading-snug">
                      {edicion.nombre}
                    </h2>

                    <div className="space-y-2.5 text-xs text-slate-600 mb-6">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-brand-cyan flex-shrink-0" />
                        <span className="font-medium text-slate-700">{edicion.sede}</span>
                      </div>
                      {edicion.fechaDesde && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-brand-cyan flex-shrink-0" />
                          <span className="font-medium text-slate-700">{new Date(edicion.fechaDesde).toLocaleDateString('es-AR')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <Link
                    to={`/evento/${edicion.idCongreso}`}
                    className="w-full py-3.5 rounded-xl bg-brand-navy hover:bg-brand-cyan text-white text-xs font-heading font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-sm hover:shadow-md group/btn relative z-10"
                  >
                    <span>Ver Sitio de esta Edición</span>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-blueprint rounded-3xl p-12 text-center max-w-lg mx-auto bg-white border border-slate-200">
              <div className="w-16 h-16 rounded-2xl bg-sky-50 border border-brand-cyan/20 flex items-center justify-center mx-auto mb-4 text-brand-cyan">
                <Calendar className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-brand-navy mb-2">No hay ediciones archivadas</h3>
              <p className="text-sm text-slate-500 mb-6 leading-relaxed">
                El histórico de congresos se irá cargando a medida que se digitalicen los eventos pasados.
              </p>
              <Link
                to="/"
                className="inline-flex items-center justify-center px-6 py-2.5 rounded-xl bg-brand-cyan hover:bg-sky-500 text-white text-xs font-bold uppercase tracking-wider transition-colors shadow-sm"
              >
                Volver a la edición actual
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};
